import type { UserProfile } from '../models/userProfile';

export interface CachedUserProfile {
  profile: UserProfile;
  timestamp: number;
  version: string;
}

export class UserProfileCacheService {
  private static readonly CACHE_KEY = 'user_profile_cache';
  private static readonly CACHE_VERSION = '1.0.0';
  private static readonly CACHE_DURATION = 24 * 60 * 60 * 1000;
  
  private static memoryCache: UserProfile | null = null;

  static setProfile(profile: UserProfile): void {
    try {
      const cachedData: CachedUserProfile = {
        profile,
        timestamp: Date.now(),
        version: this.CACHE_VERSION
      };

      this.memoryCache = profile;
      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(cachedData));
    } catch {}
  }

  static getProfile(): UserProfile | null {
    try {
      if (this.memoryCache) {
        return this.memoryCache;
      }

      const cachedDataString = sessionStorage.getItem(this.CACHE_KEY);
      if (!cachedDataString) {
        return null;
      }

      const cachedData: CachedUserProfile = JSON.parse(cachedDataString);

      if (cachedData.version !== this.CACHE_VERSION) {
        this.clearCache();
        return null;
      }

      const isExpired = Date.now() - cachedData.timestamp > this.CACHE_DURATION;
      if (isExpired) {
        this.clearCache();
        return null;
      }

      this.memoryCache = cachedData.profile;
      return cachedData.profile;
    } catch {
      this.clearCache();
      return null;
    }
  }

  static hasValidCache(): boolean {
    return this.getProfile() !== null;
  }

  static clearCache(): void {
    try {
      this.memoryCache = null;
      sessionStorage.removeItem(this.CACHE_KEY);
    } catch {}
  }

  static invalidateCache(): void {
    try {
      this.memoryCache = null;
      
      const cachedDataString = sessionStorage.getItem(this.CACHE_KEY);
      if (cachedDataString) {
        const cachedData: CachedUserProfile = JSON.parse(cachedDataString);
        cachedData.timestamp = Date.now() - this.CACHE_DURATION - 1;
        sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(cachedData));
      }
    } catch {}
  }

  static getCacheInfo(): { hasCache: boolean; cacheAge?: number; cacheVersion?: string } {
    try {
      const cachedDataString = sessionStorage.getItem(this.CACHE_KEY);
      if (!cachedDataString) {
        return { hasCache: false };
      }

      const cachedData: CachedUserProfile = JSON.parse(cachedDataString);
      return {
        hasCache: true,
        cacheAge: Date.now() - cachedData.timestamp,
        cacheVersion: cachedData.version
      };
    } catch {
      return { hasCache: false };
    }
  }
}
